import type {
  Account,
  Alliance,
  AllianceAttacksResponse,
  AllianceInvite,
  AllianceInviteListResponse,
  AllianceRank,
  AllianceSearchResponse,
  AuthResponse,
  BattleListResponse,
  BattleReport,
  BattleReportListResponse,
  BattleView,
  CancelBuildingResponse,
  CancelClaimResponse,
  CancelFleetOrderResponse,
  CancelShipProductionResponse,
  ClaimListResponse,
  ClaimQuote,
  CodexResponse,
  CollectMissionResponse,
  CreateAllianceRequest,
  CreateAllianceResponse,
  DeleteNotificationsRequest,
  DeleteNotificationsResponse,
  DispatchRequest,
  DispatchResponse,
  FacilityType,
  FeedbackRequest,
  FeedbackResponse,
  FirstGoal,
  FleetListResponse,
  FleetMovementResponse,
  FleetOrderListResponse,
  FleetOrderRequest,
  FleetOrderResponse,
  GalaxyListItem,
  GalaxyMeResponse,
  IntelReportListResponse,
  JoinGalaxyResponse,
  MapResponse,
  MarkNotificationsReadRequest,
  Membership,
  MeResponse,
  NotificationCategory,
  NotificationListResponse,
  NotificationSettings,
  OwnFleet,
  PlaceClaimRequest,
  PlaceClaimResponse,
  PlayerSearchResponse,
  PortTradeResponse,
  PublicPlayer,
  RankingsResponse,
  RegistrationInfo,
  ReinforceOptionsRequest,
  ReinforceOptionsResponse,
  RenameSystemResponse,
  Resources,
  RetreatResponse,
  RoutePreview,
  RoutePreviewRequest,
  ScoutListResponse,
  ScoutRequest,
  ScoutResponse,
  ShipProductionRequest,
  ShipProductionResponse,
  SpeedUpRequest,
  SpeedUpResponse,
  SystemResponse,
  UpdateAllianceRequest,
  UpgradeResponse,
  VisibleFleetsResponse,
} from '@stars-and-ships/api-types';
import { api } from './http';

const galaxy = (galaxyId: string) => `/galaxies/${galaxyId}`;
const system = (galaxyId: string, systemId: string) => `${galaxy(galaxyId)}/systems/${systemId}`;

export const endpoints = {
  speedUp: (galaxyId: string, body: SpeedUpRequest) =>
    api<SpeedUpResponse>('POST', `${galaxy(galaxyId)}/speed-ups`, { body }),
  codex: (galaxyId: string) => api<CodexResponse>('GET', `${galaxy(galaxyId)}/codex`),
  registrationInfo: () => api<RegistrationInfo>('GET', '/auth/registration', { auth: false }),
  register: (body: { email: string; password: string; inviteCode?: string }) =>
    api<AuthResponse>('POST', '/auth/register', { body, auth: false }),
  sendFeedback: (body: FeedbackRequest) => api<FeedbackResponse>('POST', '/feedback', { body }),
  login: (body: { email: string; password: string }) =>
    api<AuthResponse>('POST', '/auth/login', { body, auth: false }),
  verifyEmail: (token: string) =>
    api<{ emailVerified: true }>('POST', '/auth/verify-email', { body: { token }, auth: false }),
  resendVerification: () => api<Record<string, never>>('POST', '/auth/verify-email/resend'),
  forgotPassword: (email: string) =>
    api<Record<string, never>>('POST', '/auth/password/forgot', { body: { email }, auth: false }),
  resetPassword: (token: string, newPassword: string) =>
    api<{ passwordReset: true }>('POST', '/auth/password/reset', {
      body: { token, newPassword },
      auth: false,
    }),
  logout: () => api<{ loggedOut: true }>('POST', '/auth/logout'),
  me: () => api<MeResponse>('GET', '/me'),
  wsTicket: (galaxyId: string) =>
    api<{ ticket: string; expiresInSeconds: number }>('POST', '/auth/ws-ticket', {
      body: { galaxyId },
    }),

  galaxies: () => api<{ items: GalaxyListItem[] }>('GET', '/galaxies'),
  joinGalaxy: (galaxyId: string, displayName: string) =>
    api<JoinGalaxyResponse>('POST', `${galaxy(galaxyId)}/join`, { body: { displayName } }),
  galaxyMe: (galaxyId: string) => api<GalaxyMeResponse>('GET', `${galaxy(galaxyId)}/me`),
  collectMission: (galaxyId: string, goal: FirstGoal) =>
    api<CollectMissionResponse>('POST', `${galaxy(galaxyId)}/me/missions/collect`, {
      body: { goal },
    }),
  map: (galaxyId: string) => api<MapResponse>('GET', `${galaxy(galaxyId)}/map`),
  system: (galaxyId: string, systemId: string) =>
    api<SystemResponse>('GET', system(galaxyId, systemId)),

  upgrade: (galaxyId: string, systemId: string, facility: FacilityType) =>
    api<UpgradeResponse>('POST', `${system(galaxyId, systemId)}/buildings/${facility}/upgrade`),
  cancelUpgrade: (galaxyId: string, systemId: string, itemId: string) =>
    api<CancelBuildingResponse>(
      'DELETE',
      `${system(galaxyId, systemId)}/buildings/queue/${itemId}`
    ),
  startPortTrade: (galaxyId: string, systemId: string, resources: Resources) =>
    api<PortTradeResponse>('POST', `${system(galaxyId, systemId)}/port-trades`, {
      body: { resources },
    }),

  startShipProduction: (galaxyId: string, systemId: string, body: ShipProductionRequest) =>
    api<ShipProductionResponse>('POST', `${system(galaxyId, systemId)}/ship-production`, {
      body,
    }),
  cancelShipProduction: (galaxyId: string, systemId: string, itemId: string) =>
    api<CancelShipProductionResponse>(
      'DELETE',
      `${system(galaxyId, systemId)}/ship-production/${itemId}`
    ),

  fleets: (galaxyId: string) => api<FleetListResponse>('GET', `${galaxy(galaxyId)}/fleets`),
  visibleFleets: (galaxyId: string) =>
    api<VisibleFleetsResponse>('GET', `${galaxy(galaxyId)}/fleets/visible`),
  routePreview: (galaxyId: string, body: RoutePreviewRequest) =>
    api<RoutePreview>('POST', `${galaxy(galaxyId)}/fleets/route-preview`, { body }),
  dispatch: (galaxyId: string, body: DispatchRequest) =>
    api<DispatchResponse>('POST', `${galaxy(galaxyId)}/fleets`, { body }),
  recall: (galaxyId: string, fleetId: string) =>
    api<FleetMovementResponse>('POST', `${galaxy(galaxyId)}/fleets/${fleetId}/recall`),
  returnFleet: (galaxyId: string, fleetId: string) =>
    api<FleetMovementResponse>('POST', `${galaxy(galaxyId)}/fleets/${fleetId}/return`),
  shareFleet: (galaxyId: string, fleetId: string, shared: boolean) =>
    api<OwnFleet>('PATCH', `${galaxy(galaxyId)}/fleets/${fleetId}`, { body: { shared } }),

  fleetOrders: (galaxyId: string) =>
    api<FleetOrderListResponse>('GET', `${galaxy(galaxyId)}/fleet-orders`),
  placeFleetOrder: (galaxyId: string, body: FleetOrderRequest) =>
    api<FleetOrderResponse>('POST', `${galaxy(galaxyId)}/fleet-orders`, { body }),
  cancelFleetOrder: (galaxyId: string, orderId: string) =>
    api<CancelFleetOrderResponse>('DELETE', `${galaxy(galaxyId)}/fleet-orders/${orderId}`),
  shareFleetOrder: (galaxyId: string, orderId: string, shared: boolean) =>
    api<FleetOrderResponse>('PATCH', `${galaxy(galaxyId)}/fleet-orders/${orderId}`, {
      body: { shared },
    }),
  reinforceOptions: (galaxyId: string, body: ReinforceOptionsRequest) =>
    api<ReinforceOptionsResponse>('POST', `${galaxy(galaxyId)}/fleet-orders/options`, { body }),
  allianceAttacks: (galaxyId: string, allianceId: string) =>
    api<AllianceAttacksResponse>('GET', `${galaxy(galaxyId)}/alliances/${allianceId}/attacks`),

  battles: (galaxyId: string) => api<BattleListResponse>('GET', `${galaxy(galaxyId)}/battles`),
  battle: (galaxyId: string, battleId: string) =>
    api<BattleView>('GET', `${galaxy(galaxyId)}/battles/${battleId}`),
  retreat: (galaxyId: string, battleId: string) =>
    api<RetreatResponse>('POST', `${galaxy(galaxyId)}/battles/${battleId}/retreat`),
  battleReports: (galaxyId: string) =>
    api<BattleReportListResponse>('GET', `${galaxy(galaxyId)}/battle-reports`),
  battleReport: (galaxyId: string, battleId: string) =>
    api<BattleReport>('GET', `${galaxy(galaxyId)}/battle-reports/${battleId}`),

  scouts: (galaxyId: string) => api<ScoutListResponse>('GET', `${galaxy(galaxyId)}/scouts`),
  sendScout: (galaxyId: string, body: ScoutRequest) =>
    api<ScoutResponse>('POST', `${galaxy(galaxyId)}/scouts`, { body }),
  intelReports: (galaxyId: string, systemId?: string) =>
    api<IntelReportListResponse>(
      'GET',
      `${galaxy(galaxyId)}/intel-reports${systemId ? `?systemId=${systemId}` : ''}`
    ),
  claimQuote: (galaxyId: string, systemId: string) =>
    api<ClaimQuote>('GET', `${galaxy(galaxyId)}/claims/quote?systemId=${systemId}`),
  placeClaim: (galaxyId: string, body: PlaceClaimRequest) =>
    api<PlaceClaimResponse>('POST', `${galaxy(galaxyId)}/claims`, { body }),
  claims: (galaxyId: string, scope: 'mine' | 'incoming') =>
    api<ClaimListResponse>('GET', `${galaxy(galaxyId)}/claims?scope=${scope}`),
  cancelClaim: (galaxyId: string, claimId: string) =>
    api<CancelClaimResponse>('DELETE', `${galaxy(galaxyId)}/claims/${claimId}`),

  alliances: (galaxyId: string, search: string) =>
    api<AllianceSearchResponse>(
      'GET',
      `${galaxy(galaxyId)}/alliances${search ? `?search=${encodeURIComponent(search)}` : ''}`
    ),
  alliance: (galaxyId: string, allianceId: string) =>
    api<Alliance>('GET', `${galaxy(galaxyId)}/alliances/${allianceId}`),
  createAlliance: (galaxyId: string, body: CreateAllianceRequest) =>
    api<CreateAllianceResponse>('POST', `${galaxy(galaxyId)}/alliances`, { body }),
  updateAlliance: (galaxyId: string, allianceId: string, body: UpdateAllianceRequest) =>
    api<Alliance>('PATCH', `${galaxy(galaxyId)}/alliances/${allianceId}`, { body }),
  disbandAlliance: (galaxyId: string, allianceId: string) =>
    api<void>('DELETE', `${galaxy(galaxyId)}/alliances/${allianceId}`),
  allianceInvites: (galaxyId: string, allianceId: string) =>
    api<AllianceInviteListResponse>('GET', `${galaxy(galaxyId)}/alliances/${allianceId}/invites`),
  myAllianceInvites: (galaxyId: string) =>
    api<AllianceInviteListResponse>('GET', `${galaxy(galaxyId)}/alliance-invites`),
  invitePlayer: (galaxyId: string, allianceId: string, playerId: string) =>
    api<{ invite: AllianceInvite }>('POST', `${galaxy(galaxyId)}/alliances/${allianceId}/invites`, {
      body: { playerId },
    }),
  applyToAlliance: (galaxyId: string, allianceId: string, message: string) =>
    api<{ application: AllianceInvite }>(
      'POST',
      `${galaxy(galaxyId)}/alliances/${allianceId}/applications`,
      { body: message ? { message } : {} }
    ),
  acceptInvite: (galaxyId: string, inviteId: string) =>
    api<{ membership: Membership }>(
      'POST',
      `${galaxy(galaxyId)}/alliance-invites/${inviteId}/accept`
    ),
  declineInvite: (galaxyId: string, inviteId: string) =>
    api<void>('POST', `${galaxy(galaxyId)}/alliance-invites/${inviteId}/decline`),
  changeRank: (
    galaxyId: string,
    allianceId: string,
    playerId: string,
    rank: Exclude<AllianceRank, 'overlord'>
  ) =>
    api<{ membership: Membership }>(
      'PUT',
      `${galaxy(galaxyId)}/alliances/${allianceId}/members/${playerId}/rank`,
      { body: { rank } }
    ),
  removeMember: (galaxyId: string, allianceId: string, playerId: string) =>
    api<void>('DELETE', `${galaxy(galaxyId)}/alliances/${allianceId}/members/${playerId}`),
  transferLeadership: (galaxyId: string, allianceId: string, playerId: string) =>
    api<{ memberships: Membership[] }>(
      'POST',
      `${galaxy(galaxyId)}/alliances/${allianceId}/leadership`,
      { body: { playerId } }
    ),
  publicPlayer: (galaxyId: string, playerId: string) =>
    api<PublicPlayer>('GET', `${galaxy(galaxyId)}/players/${playerId}`),
  rankings: (galaxyId: string) => api<RankingsResponse>('GET', `${galaxy(galaxyId)}/rankings`),
  renameSystem: (galaxyId: string, systemId: string, name: string | null) =>
    api<RenameSystemResponse>('PATCH', `${galaxy(galaxyId)}/systems/${systemId}/name`, {
      body: { name },
    }),
  searchPlayers: (galaxyId: string, search: string) =>
    api<PlayerSearchResponse>(
      'GET',
      `${galaxy(galaxyId)}/players?search=${encodeURIComponent(search)}`
    ),

  notifications: (galaxyId: string, category: NotificationCategory) =>
    api<NotificationListResponse>('GET', `${galaxy(galaxyId)}/notifications?category=${category}`),
  markNotificationsRead: (galaxyId: string, body: MarkNotificationsReadRequest) =>
    api<{ unreadCount: number }>('POST', `${galaxy(galaxyId)}/notifications/read`, { body }),
  deleteNotifications: (galaxyId: string, body: DeleteNotificationsRequest) =>
    api<DeleteNotificationsResponse>('POST', `${galaxy(galaxyId)}/notifications/delete`, { body }),
  notificationSettings: () => api<NotificationSettings>('GET', '/me/notification-settings'),
  putNotificationSettings: (body: NotificationSettings) =>
    api<NotificationSettings>('PUT', '/me/notification-settings', { body }),
};

export type { Account };
